import { useCallback, useEffect, useRef, useState } from 'react';
import { listSucursales } from '../services/data';
import { diag } from '../services/diagnostico';

// Avisar a todos los useSucursales (menú lateral, selectores…) que la lista cambió,
// p. ej. después de guardar un logo, para que se recarguen sin F5.
const EVENTO = 'sucursales:changed';
export const notificarSucursalesCambiadas = () => window.dispatchEvent(new Event(EVENTO));
const ESPERAS_MS = [0, 1000, 3000]; // reintento controlado ante fallos de red/servidor (sin bucles)
const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Sucursales visibles para el usuario autenticado y el estado de la carga ('cargando' | 'ok' | 'error').
 * `recargar()` vuelve a intentarlo (botón Reintentar). Un fallo no se convierte en "lista vacía": queda en 'error'.
 */
export function useSucursalesEstado({ activo = true } = {}) {
  const [estado, setEstado] = useState({ sucursales: [], carga: 'cargando' });
  const vivo = useRef(true);
  const cargar = useCallback(async () => {
    setEstado((x) => (x.carga === 'ok' ? x : { ...x, carga: 'cargando' }));
    let ultimo;
    for (let i = 0; i < ESPERAS_MS.length; i++) {
      if (ESPERAS_MS[i]) await esperar(ESPERAS_MS[i]);
      try {
        const rows = await listSucursales();
        if (vivo.current) setEstado({ carga: 'ok', sucursales: rows.map((v) => ({
          id: v.id,
          nombre: v.ubicacion || v.nombre || v.id,
          ubicacion: v.ubicacion || '',
          logoUrl: v.logoUrl || null,
          activo: v.activo !== false,
        })) });
        return;
      } catch (e) {
        ultimo = e;
        diag('sucursales', 'fallo al cargar', { status: e?.status || 'red', intento: i + 1 });
        if (e?.status && e.status < 500 && e.status !== 408 && e.status !== 429) break; // 401/403: no se reintenta
      }
    }
    console.error(ultimo);
    if (vivo.current) setEstado((x) => ({ ...x, carga: x.carga === 'ok' ? 'ok' : 'error' }));
  }, []);
  useEffect(() => {
    vivo.current = true;
    if (!activo) return () => { vivo.current = false; };
    cargar();
    window.addEventListener(EVENTO, cargar);
    return () => { vivo.current = false; window.removeEventListener(EVENTO, cargar); };
  }, [activo, cargar]);
  return { ...estado, recargar: cargar };
}

export function useSucursales() {
  return useSucursalesEstado().sucursales;
}
